import React, { useState } from 'react';
import { TrendingUpIcon, UsersIcon, MailOpenIcon, MessageSquareIcon, PhoneOffIcon, StarIcon, MapPinIcon, CheckCircle2Icon, ArrowRightIcon, SparklesIcon, BarChart3Icon, FilterIcon, XIcon, SendIcon, MousePointerClickIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { getCategoryLabel } from '../data/communications';
import type { ChannelKey, Notice } from '../types/notice';
const CHANNEL_LABELS: Record<ChannelKey, {
  fr: string;
  en: string;
}> = {
  email: {
    fr: 'Courriel',
    en: 'Email'
  },
  sms: {
    fr: 'SMS',
    en: 'SMS'
  },
  voice: {
    fr: 'Voix automatisée',
    en: 'Automated voice'
  },
  push: {
    fr: 'Notification Push',
    en: 'Push notification'
  },
  website: {
    fr: 'Site web',
    en: 'Website'
  },
  social: {
    fr: 'Réseaux sociaux',
    en: 'Social media'
  },
  screens: {
    fr: 'Écrans publics',
    en: 'Public screens'
  }
};
interface AnalyticsDashboardProps {
  /** When set, the dashboard reports on this single notice. */
  focusedNotice?: Notice | null;
  onClearFocus?: () => void;
}

/** Compact metric tile shared by both dashboard modes. */
function MetricCard({
  icon: Icon,
  label,
  value,
  hint,
  hintTone = 'muted',
  pending
}: {
  icon: typeof TrendingUpIcon;
  label: string;
  value: string;
  hint?: string;
  hintTone?: 'muted' | 'positive';
  /** Replaces the figure with a "available once sent" placeholder. */
  pending?: string;
}) {
  return <div className={`p-5 rounded-xl shadow-sm border ${pending ? 'bg-slate-50 border-slate-200' : 'bg-white border-slate-100'}`} data-id="element-34" data-mp="1j53r8kb0ih82">
      <div className="flex items-center gap-3 mb-2" data-id="element-35" data-mp="1a6i92q9gd47t">
        <div className={`p-2 rounded-lg ${pending ? 'bg-slate-200 text-slate-400' : 'bg-indigo-50 text-[#4F46E5]'}`} data-id="element-36" data-mp="1a8356kbf0v7s">
          <Icon className="w-5 h-5" data-id="element-37" data-mp="34l2qmu2h8jmz" />
        </div>
        <h3 className="text-sm font-medium text-slate-600" data-id="element-38" data-mp="34i6tv92d8104">{label}</h3>
      </div>
      {pending ? <p className="text-sm text-slate-400 italic pt-1.5" data-id="element-39" data-mp="38ja5fjddravj">{pending}</p> : <div className="flex items-end gap-3 flex-wrap" data-id="element-40" data-mp="lb2r94pn7wte">
          <span className="text-3xl font-bold text-[#0F172A] font-heading" data-id="element-41" data-mp="gd168e8npnfa">
            {value}
          </span>
          {hint && <span className={`text-sm mb-1 ${hintTone === 'positive' ? 'font-medium text-emerald-600' : 'text-slate-500'}`} data-id="element-42" data-mp="celclri2lgsw">
              {hint}
            </span>}
        </div>}
    </div>;
}
export function AnalyticsDashboard({
  focusedNotice = null,
  onClearFocus
}: AnalyticsDashboardProps) {
  const {
    t,
    lang
  } = useLanguage();
  const [selectedSector, setSelectedSector] = useState('A');
  const locale = lang === 'fr' ? 'fr-CA' : 'en-CA';

  // ---------------------------------------------------------------- FOCUSED
  if (focusedNotice) {
    const stats = focusedNotice.stats;
    // Not sent yet: the layout still shows, with each figure marked as pending.
    const pending = stats ? undefined : t('Disponible après l’envoi', 'Available once sent');
    return <div className="space-y-8 animate-in fade-in duration-300" data-id="element-43" data-mp="1vd9k4ruiuden">
        <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-5 flex flex-col sm:flex-row sm:items-center gap-4 justify-between" data-id="element-44" data-mp="209grmp2kfwyp">
          <div className="min-w-0" data-id="element-45" data-mp="249ikq9u8ji2d">
            <div className="flex items-center gap-2 flex-wrap mb-1.5" data-id="element-46" data-mp="29bpyn4yml87c">
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-white text-[#4F46E5] text-xs font-semibold border border-indigo-200" data-id="element-47" data-mp="2ea17bcd6pcnz">
                <FilterIcon className="w-3 h-3" data-id="element-48" data-mp="2j1jb086ldrcn" />
                {t('Filtré sur un avis', 'Filtered on one notice')}
              </span>
              <span className="px-2 py-0.5 rounded-full bg-white text-slate-600 text-xs font-medium border border-slate-200" data-id="element-49" data-mp="2s1pkzk330972">
                {getCategoryLabel(focusedNotice.category)[lang]}
              </span>
              <span className="text-xs text-slate-500" data-id="element-50" data-mp="2jda5q720901l">{focusedNotice.id}</span>
            </div>
            <h2 className="text-lg font-bold text-[#0F172A] font-heading truncate" data-id="element-51" data-mp="2fcqa57x2y4hk">
              {focusedNotice.title}
            </h2>
            <p className="text-sm text-slate-500 mt-0.5" data-id="element-52" data-mp="2t33ihbdvvl7f">
              {focusedNotice.status === 'scheduled' ? t('Envoi prévu le ', 'Sending on ') : focusedNotice.status === 'draft' ? t('Brouillon modifié le ', 'Draft edited on ') : t('Envoyé le ', 'Sent on ')}
              {new Date(focusedNotice.date).toLocaleDateString(locale, {
              day: 'numeric',
              month: 'long',
              year: 'numeric'
            })}
              {focusedNotice.audience.detail ? ` · ${focusedNotice.audience.detail[lang]}` : ''}
            </p>
          </div>
          <button onClick={onClearFocus} className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors" data-id="element-53" data-mp="3gtz8q42oy8ck">
            <XIcon className="w-4 h-4" data-id="element-54" data-mp="17i58ioka4dk5" />
            {t('Voir tout le module', 'View the whole module')}
          </button>
        </div>

        {pending && <p className="flex items-start gap-2 text-sm text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-4 py-3" data-id="element-55" data-mp="ykmkdbbc9j41">
            <BarChart3Icon className="w-4 h-4 mt-0.5 text-slate-400 flex-shrink-0" data-id="element-56" data-mp="1mbrvxea8xp8j" />
            {t('Cet avis n’a pas encore été diffusé. L’audience et les canaux sont déjà connus ; les mesures de performance apparaîtront après l’envoi.', 'This notice has not gone out yet. Its audience and channels are already known; performance figures will appear once sent.')}
          </p>}

        <>
            <section data-id="element-57" data-mp="29z8cjjo5fvjn">
              <h3 className="text-lg font-bold text-[#0F172A] font-heading mb-4 flex items-center gap-2" data-id="element-58" data-mp="2a0jhhmtbz9cc">
                <BarChart3Icon className="w-5 h-5 text-[#4F46E5]" data-id="element-59" data-mp="2jugrb20ljq3s" />
                {t('Performance de cet avis', 'Performance of this notice')}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-60" data-mp="29vzw1sumkpbx">
                <MetricCard icon={SendIcon} label={t('Destinataires', 'Recipients')} value={stats ? stats.recipients.toLocaleString(locale) : focusedNotice.audience.count.toLocaleString(locale)} hint={stats ? `${stats.delivered.toLocaleString(locale)} ${t('livrés', 'delivered')}` : t('audience ciblée', 'targeted audience')} data-id="element-61" data-mp="24y7rnzcsnaqu" />
                <MetricCard icon={MailOpenIcon} label={t("Taux d'ouverture", 'Open rate')} value={stats ? `${stats.openRate} %` : '—'} hint={t('tous canaux confondus', 'across all channels')} pending={pending} data-id="element-62" data-mp="1d6if2hq6zgux" />
                <MetricCard icon={MousePointerClickIcon} label={t('Taux de clics', 'Click rate')} value={stats ? `${stats.clickRate} %` : '—'} hint={t('sur les liens de l’avis', 'on the notice links')} pending={pending} data-id="element-63" data-mp="1oo0k1x6f3zo" />
                <MetricCard icon={MessageSquareIcon} label={t('Interactions citoyennes', 'Citizen interactions')} value={stats ? stats.interactions.toLocaleString(locale) : '—'} hint={t('clics, réponses, questions', 'clicks, replies, questions')} pending={pending} data-id="element-64" data-mp="3a5r50d4nc79n" />
                <MetricCard icon={PhoneOffIcon} label={t('Appels évités', 'Calls avoided')} value={stats ? stats.callsAvoided.toLocaleString(locale) : '—'} hint={t('estimé par ATNA', 'estimated by ATNA')} pending={pending} data-id="element-65" data-mp="2rdy9aay707y8" />
                <MetricCard icon={StarIcon} label={t('Satisfaction', 'Satisfaction')} value={stats ? `${stats.satisfaction.toLocaleString(locale)} / 5` : '—'} hint={t('sur cet avis', 'for this notice')} pending={pending} data-id="element-66" data-mp="1tsn6ffo8mbef" />
              </div>
            </section>

            <section className="bg-white p-6 rounded-xl shadow-sm border border-slate-100" data-id="element-67" data-mp="2cil9tih3bgbq">
              <h3 className="text-lg font-bold text-[#0F172A] font-heading mb-6" data-id="element-68" data-mp="28g86f0ptfui4">
                {t('Performance par canal', 'Performance by channel')}
              </h3>
              <div className="overflow-x-auto" data-id="element-69" data-mp="2me6fprpa50qr">
                <table className="w-full text-sm text-left" data-id="element-70" data-mp="2hihe7itp99rt">
                  <thead className="text-xs text-slate-500 uppercase bg-slate-50" data-id="element-71" data-mp="313hlzqyllpeb">
                    <tr data-id="element-72" data-mp="311dllizwr8is">
                      <th className="px-4 py-3 rounded-l-lg" data-id="element-73" data-mp="362ukxa2qumx9">
                        {t('Canal', 'Channel')}
                      </th>
                      <th className="px-4 py-3" data-id="element-74" data-mp="2r4in303mx84i">
                        {t('Messages envoyés', 'Messages sent')}
                      </th>
                      <th className="px-4 py-3" data-id="element-75" data-mp="2icqg4zdtkh4o">
                        {t("Taux d'ouverture", 'Open rate')}
                      </th>
                      <th className="px-4 py-3 rounded-r-lg" data-id="element-76" data-mp="2n4i9tis9v2z1">
                        {t('Taux de clics', 'Click rate')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100" data-id="element-77" data-mp="2dcu90r9ts9kw">
                    {stats ? stats.channels.map(c => <tr key={c.channel} data-id="element-78" data-mp="1zfyysvhu8c39">
                            <td className="px-4 py-3 font-medium text-slate-900" data-id="element-79" data-mp="23fpqd1f6uw8u">
                              {CHANNEL_LABELS[c.channel][lang]}
                            </td>
                            <td className="px-4 py-3 text-slate-600" data-id="element-80" data-mp="2s83esjwf59ts">
                              {c.sent > 0 ? c.sent.toLocaleString(locale) : '—'}
                            </td>
                            <td className="px-4 py-3" data-id="element-81" data-mp="2ia7947m93732">
                              <div className="flex items-center gap-2" data-id="element-82" data-mp="2iapdxsmzatml">
                                <span className="w-10" data-id="element-83" data-mp="35zi2v556o17p">{c.openRate} %</span>
                                <div className="w-24 h-2 bg-slate-100 rounded-full overflow-hidden" data-id="element-84" data-mp="3ayl8aqp2s9g7">
                                  <div className="h-full bg-[#4F46E5]" style={{
                          width: `${c.openRate}%`
                        }} data-id="element-85" data-mp="2x7ptkxrway4p" />
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3" data-id="element-86" data-mp="3tpm2z2j5xity">
                              <div className="flex items-center gap-2" data-id="element-87" data-mp="3tr6zuysy9duz">
                                <span className="w-10" data-id="element-88" data-mp="1i8sowzi8w83u">{c.clickRate} %</span>
                                <div className="w-24 h-2 bg-slate-100 rounded-full overflow-hidden" data-id="element-89" data-mp="1da91euo06o9d">
                                  <div className="h-full bg-emerald-500" style={{
                          width: `${c.clickRate}%`
                        }} data-id="element-90" data-mp="1rz56ciurjl5r" />
                                </div>
                              </div>
                            </td>
                          </tr>) : focusedNotice.channels.map(c => <tr key={c} data-id="element-91" data-mp="197m4ifch6d5b">
                            <td className="px-4 py-3 font-medium text-slate-900" data-id="element-92" data-mp="14a2q9ffs20dd">
                              {CHANNEL_LABELS[c][lang]}
                            </td>
                            <td className="px-4 py-3 text-slate-400 italic" colSpan={3} data-id="element-93" data-mp="2kqorw9eu1bax">
                              {pending}
                            </td>
                          </tr>)}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="bg-white p-6 rounded-xl shadow-sm border border-slate-100" data-id="element-94" data-mp="2wpappujndvht">
              <h3 className="text-lg font-bold text-[#0F172A] font-heading mb-4" data-id="element-95" data-mp="31o5fjgg18kbf">
                {t('Audience ciblée', 'Targeted audience')}
              </h3>
              <div className="flex items-center gap-3 flex-wrap" data-id="element-96" data-mp="292m00moecyqn">
                <span className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 text-sm text-slate-700" data-id="element-97" data-mp="2e0xc7425zlc1">
                  <MapPinIcon className="w-4 h-4 text-slate-400" data-id="element-98" data-mp="2isx5idnqbfzn" />
                  {focusedNotice.audience.detail?.[lang] ?? t("Toute l'organisation", 'Entire organization')}
                </span>
                <span className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 text-sm text-slate-700" data-id="element-99" data-mp="1q8ylpcu59jeb">
                  <UsersIcon className="w-4 h-4 text-slate-400" data-id="element-100" data-mp="3e3i9toprvor" />
                  {focusedNotice.audience.count.toLocaleString(locale)}{' '}
                  {t('citoyens', 'citizens')}
                </span>
                {focusedNotice.channels.map(c => <span key={c} className="px-3 py-2 rounded-lg bg-indigo-50 border border-indigo-100 text-sm text-[#4F46E5] font-medium" data-id="element-101" data-mp="3pnxsuoz1y5zu">
                    {CHANNEL_LABELS[c][lang]}
                  </span>)}
              </div>
            </section>
        </>
      </div>;
  }

  // ----------------------------------------------------------------- GLOBAL
  const sectors = [{
    id: 'A',
    status: 'green',
    citizens: 12450,
    cards: 9820,
    openRate: 78,
    notices: 45,
    questions: 342
  }, {
    id: 'B',
    status: 'yellow',
    citizens: 8210,
    cards: 5100,
    openRate: 62,
    notices: 38,
    questions: 512
  }, {
    id: 'C',
    status: 'green',
    citizens: 15300,
    cards: 13900,
    openRate: 85,
    notices: 52,
    questions: 215
  }, {
    id: 'D',
    status: 'red',
    citizens: 6800,
    cards: 2850,
    openRate: 45,
    notices: 25,
    questions: 890
  }, {
    id: 'E',
    status: 'yellow',
    citizens: 9100,
    cards: 5200,
    openRate: 55,
    notices: 30,
    questions: 420
  }];
  const sectorName = (id: string) => `${t('Secteur', 'District')} ${id}`;
  const active = sectors.find(s => s.id === selectedSector) ?? sectors[0];
  const channelRows: {
    channel: ChannelKey | 'atna';
    sent: string;
    open: number;
    interact: number;
    best?: boolean;
  }[] = [{
    channel: 'push',
    sent: '85,420',
    open: 88,
    interact: 24,
    best: true
  }, {
    channel: 'sms',
    sent: '42,100',
    open: 92,
    interact: 18
  }, {
    channel: 'email',
    sent: '112,500',
    open: 45,
    interact: 8
  }, {
    channel: 'voice',
    sent: '8,400',
    open: 75,
    interact: 5
  }, {
    channel: 'atna',
    sent: '15,200',
    open: 100,
    interact: 42
  }, {
    channel: 'website',
    sent: '—',
    open: 35,
    interact: 12
  }, {
    channel: 'social',
    sent: '—',
    open: 22,
    interact: 6
  }];
  const funnel = [{
    label: t('Avis créés', 'Notices created'),
    value: '1,245',
    percent: 100,
    color: 'bg-slate-800'
  }, {
    label: t('Avis envoyés', 'Notices sent'),
    value: '125,842',
    percent: 95,
    color: 'bg-[#4F46E5]'
  }, {
    label: t('Ouverts', 'Opened'),
    value: '91,864',
    percent: 73,
    color: 'bg-indigo-400'
  }, {
    label: t('Cliqués', 'Clicked'),
    value: '34,210',
    percent: 27,
    color: 'bg-indigo-300'
  }, {
    label: t('Questions posées', 'Questions asked'),
    value: '8,450',
    percent: 6.7,
    color: 'bg-emerald-400'
  }, {
    label: t('Actions complétées', 'Actions completed'),
    value: '5,120',
    percent: 4.1,
    color: 'bg-emerald-500'
  }];
  const topics = [{
    topic: t('Déneigement', 'Snow removal'),
    sent: '45k',
    open: '88%',
    click: '34%',
    q: 1240
  }, {
    topic: t('Collecte des déchets', 'Waste collection'),
    sent: '112k',
    open: '75%',
    click: '12%',
    q: 890
  }, {
    topic: t('Travaux routiers', 'Road works'),
    sent: '28k',
    open: '82%',
    click: '41%',
    q: 2100
  }, {
    topic: t("Restrictions d'eau", 'Water restrictions'),
    sent: '15k',
    open: '91%',
    click: '22%',
    q: 450
  }, {
    topic: t('Taxes', 'Taxes'),
    sent: '85k',
    open: '65%',
    click: '18%',
    q: 1850
  }, {
    topic: t('Permis', 'Permits'),
    sent: '12k',
    open: '58%',
    click: '25%',
    q: 920
  }, {
    topic: t('Loisirs', 'Recreation'),
    sent: '65k',
    open: '42%',
    click: '15%',
    q: 310
  }, {
    topic: t('Bibliothèques', 'Libraries'),
    sent: '32k',
    open: '38%',
    click: '8%',
    q: 120
  }];
  const questions = [t('Quand est ma prochaine collecte ?', 'When is my next collection?'), t('Fermetures de routes', 'Road closures'), t('Règles de stationnement', 'Parking rules'), t('Taxes municipales', 'Municipal taxes'), t('Demande de permis', 'Permit application'), t('Piscines publiques', 'Public pools'), t('Services de bibliothèque', 'Library services'), t('Gestion des matières résiduelles', 'Waste management'), t("Avis d'urgence", 'Emergency notices'), t('Programmes de loisirs', 'Recreation programs')];
  return <div className="space-y-8 animate-in fade-in duration-300" data-id="element-102" data-mp="2116bb9sycrob">
      {/* Executive summary */}
      <section data-id="element-103" data-mp="1w3wgfemsnnt3">
        <div className="flex items-center justify-between mb-4 gap-4 flex-wrap" data-id="element-104" data-mp="1w3d4cejtrtmz">
          <h2 className="text-lg font-bold text-[#0F172A] font-heading flex items-center gap-2" data-id="element-105" data-mp="1s2b2dpyix3hb">
            <BarChart3Icon className="w-5 h-5 text-[#4F46E5]" data-id="element-106" data-mp="2oqh70cvg3vvl" />
            {t('Sommaire exécutif', 'Executive summary')}
          </h2>
          <div className="flex items-center gap-2 text-sm text-slate-500" data-id="element-107" data-mp="2juikkutognxt">
            <FilterIcon className="w-4 h-4" data-id="element-108" data-mp="2f05z6f8n52jf" />
            <span data-id="element-109" data-mp="2b03zucqdol7b">{t('30 derniers jours', 'Last 30 days')}</span>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-110" data-mp="37i04k46pahq8">
          <MetricCard icon={TrendingUpIcon} label={t('Avis envoyés', 'Notices sent')} value="125,842" hint={t('+18 % vs mois préc.', '+18% vs. prev. month')} hintTone="positive" data-id="element-111" data-mp="37k4lbmpp7b7x" />
          <MetricCard icon={UsersIcon} label={t('Citoyens atteints', 'Citizens reached')} value="91 %" hint={t('des citoyens inscrits', 'of registered citizens')} data-id="element-112" data-mp="3r6ah0uxgzo2h" />
          <MetricCard icon={MailOpenIcon} label={t("Taux d'ouverture", 'Open rate')} value="73 %" hint={t('tous canaux confondus', 'across all channels')} data-id="element-113" data-mp="3hh0ndamz9xc9" />
          <MetricCard icon={MessageSquareIcon} label={t('Interactions citoyennes', 'Citizen interactions')} value="12,487" hint={t('clics, réponses, questions', 'clicks, replies, questions')} data-id="element-114" data-mp="dyqxkv6j6r41" />
          <MetricCard icon={PhoneOffIcon} label={t('Appels évités', 'Calls avoided')} value="2,134" hint={t('estimé par ATNA', 'estimated by ATNA')} data-id="element-115" data-mp="2jryyqabkom70" />
          <MetricCard icon={StarIcon} label={t('Satisfaction', 'Satisfaction')} value="4.7 / 5" hint={t('communications', 'communications')} data-id="element-116" data-mp="16t5h7sgrzf5o" />
        </div>
      </section>

      {/* Recommendations & value */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-117" data-mp="1bt1n5x919gnv">
        <section className="lg:col-span-2" data-id="element-118" data-mp="1btt90gctw00y">
          <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-6 h-full relative overflow-hidden" data-id="element-119" data-mp="o37bhj0g9y57">
            <div className="absolute top-0 right-0 p-6 opacity-10" data-id="element-120" data-mp="j55jg8l1qohl">
              <SparklesIcon className="w-32 h-32 text-[#4F46E5]" data-id="element-121" data-mp="wvr33zibr2gj" />
            </div>
            <div className="relative z-10" data-id="element-122" data-mp="wxvibdlobnw4">
              <div className="flex items-center gap-2 mb-4" data-id="element-123" data-mp="s4skgt9v5sa4">
                <SparklesIcon className="w-6 h-6 text-[#4F46E5]" data-id="element-124" data-mp="s37px3nju9c9" />
                <h2 className="text-xl font-bold text-[#0F172A] font-heading" data-id="element-125" data-mp="4dwzog3trxcd">
                  {t('Recommandations ATNA', 'ATNA recommendations')}
                </h2>
              </div>

              <div className="bg-white/80 backdrop-blur-sm rounded-lg p-5 border border-indigo-100/50 mb-5" data-id="element-126" data-mp="2qlw944suzqdp">
                <p className="text-slate-700 font-medium mb-1" data-id="element-127" data-mp="2ml2k183ok5j8">
                  {t('Le Secteur A montre une forte portée numérique (83 %).', 'District A shows strong digital reach (83%).')}
                </p>
                <p className="text-slate-600 text-sm mb-4" data-id="element-128" data-mp="3a80wluooitbv">
                  {t("Les requêtes concernant l'entretien routier sont en hausse dans cette zone.", 'Requests about road maintenance are rising in this area.')}
                </p>

                <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2" data-id="element-129" data-mp="3ebqkehxj56vu">
                  {t('Action recommandée', 'Recommended action')}
                </h4>
                <ul className="space-y-2 mb-4" data-id="element-130" data-mp="1jx656krza3ju">
                  {[t('Envoyer un avis ciblé', 'Send a targeted notice'), t('Utiliser Push + SMS', 'Use Push + SMS'), t('Notifier uniquement le secteur affecté', 'Notify only the affected district')].map(item => <li key={item} className="flex items-center gap-2 text-sm text-slate-700" data-id="element-131" data-mp="3qs81tixgzt8f">
                      <CheckCircle2Icon className="w-4 h-4 text-emerald-500" data-id="element-132" data-mp="3hslajpygg3t3" />
                      {item}
                    </li>)}
                </ul>

                <div className="flex items-center gap-4 p-3 bg-emerald-50 rounded-lg border border-emerald-100" data-id="element-133" data-mp="33uwoiesniqmg">
                  <div className="flex-1" data-id="element-134" data-mp="37w8ggqyzznxt">
                    <div className="text-xs font-bold text-emerald-800 uppercase tracking-wider mb-1" data-id="element-135" data-mp="122i9bnhhs09q">
                      {t('Impact prévu', 'Expected impact')}
                    </div>
                    <div className="flex gap-4 text-sm font-medium text-emerald-700 flex-wrap" data-id="element-136" data-mp="2f091i1um7cx9">
                      <span data-id="element-137" data-mp="2a0e7hpfjm77z">
                        ↓ 22 % {t("d'appels entrants", 'inbound calls')}
                      </span>
                      <span data-id="element-138" data-mp="25zudguq9njq2">↑ 18 % {t('de satisfaction', 'satisfaction')}</span>
                    </div>
                  </div>
                </div>
              </div>

              <button className="flex items-center gap-2 bg-[#4F46E5] hover:bg-[#4338CA] text-white px-5 py-2.5 rounded-lg font-medium transition-colors shadow-sm shadow-indigo-200" data-id="element-139" data-mp="2xqqf0z6vm1y1">
                {t("Préparer l'avis citoyen", 'Prepare citizen notice')}
                <ArrowRightIcon className="w-4 h-4" data-id="element-140" data-mp="2ow2cvhwll0dg" />
              </button>
            </div>
          </div>
        </section>

        <section className="lg:col-span-1" data-id="element-141" data-mp="3he7nows3k0nm">
          <div className="bg-[#0F172A] rounded-xl p-6 h-full text-white shadow-lg relative overflow-hidden" data-id="element-142" data-mp="3hg0uqd6mgmw5">
            <h2 className="text-xl font-bold font-heading mb-6" data-id="element-143" data-mp="3cmg3r53g4t6g">
              {t('Valeur générée par bciti+', 'Value generated by bciti+')}
            </h2>

            <div className="space-y-5 relative z-10" data-id="element-144" data-mp="3ox74qvs35g3t">
              <div data-id="element-145" data-mp="3p034mpnhueuw">
                <div className="text-3xl font-bold text-[#818CF8] mb-1" data-id="element-146" data-mp="3txblmmwwocuk">
                  87 000 $
                </div>
                <div className="text-sm text-slate-300" data-id="element-147" data-mp="1tj43oxtb8la">
                  {t('Économies opérationnelles estimées', 'Estimated operational savings')}
                </div>
              </div>

              <div className="h-px bg-slate-700/50" data-id="element-148" data-mp="bpdvp9k0ke07" />

              <ul className="space-y-3" data-id="element-149" data-mp="gj8apzooc4sy">
                {[{
                label: t('Appels évités', 'Calls avoided'),
                value: '2,134'
              }, {
                label: t('Courriels évités', 'Emails avoided'),
                value: '847'
              }, {
                label: t('Visites au comptoir évitées', 'Counter visits avoided'),
                value: '319'
              }, {
                label: t('Heures de travail sauvées', 'Work hours saved'),
                value: '147 h'
              }].map(row => <li key={row.label} className="flex items-center justify-between gap-3" data-id="element-150" data-mp="qlkao4u9ng9s">
                    <span className="text-sm text-slate-300" data-id="element-151" data-mp="lm7ntk3ea977">{row.label}</span>
                    <span className="font-bold" data-id="element-152" data-mp="loahqf7o4mh7">{row.value}</span>
                  </li>)}
              </ul>
            </div>
          </div>
        </section>
      </div>

      {/* Channel performance */}
      <section className="bg-white p-6 rounded-xl shadow-sm border border-slate-100" data-id="element-153" data-mp="7u9lbs46vbk0">
        <h2 className="text-lg font-bold text-[#0F172A] font-heading mb-6" data-id="element-154" data-mp="2vga66cdjwn0">
          {t('Performance par canal', 'Performance by channel')}
        </h2>
        <div className="overflow-x-auto" data-id="element-155" data-mp="1j852t7ikfwee">
          <table className="w-full text-sm text-left" data-id="element-156" data-mp="1ealoy8ps5lmz">
            <thead className="text-xs text-slate-500 uppercase bg-slate-50" data-id="element-157" data-mp="1ea3k4np1xz3g">
              <tr data-id="element-158" data-mp="2w76yrlp4wgt1">
                <th className="px-4 py-3 rounded-l-lg" data-id="element-159" data-mp="2w6oty0oeou9i">{t('Canal', 'Channel')}</th>
                <th className="px-4 py-3" data-id="element-160" data-mp="318e47e3t13wj">{t('Messages envoyés', 'Messages sent')}</th>
                <th className="px-4 py-3" data-id="element-161" data-mp="2n8btqcxttcrb">{t("Taux d'ouverture", 'Open rate')}</th>
                <th className="px-4 py-3 rounded-r-lg" data-id="element-162" data-mp="2s6ddl032tg3u">
                  {t("Taux d'interaction", 'Interaction rate')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-163" data-mp="3fylh7eb1lupu">
              {channelRows.map(row => <tr key={row.channel} className={row.best ? 'bg-indigo-50/30' : ''} data-id="element-164" data-mp="366fp84ksjdan">
                  <td className="px-4 py-3 font-medium text-slate-900" data-id="element-165" data-mp="364bk5jb5o88s">
                    <span className="flex items-center gap-2" data-id="element-166" data-mp="3b3xzpzegi0kr">
                      {row.channel === 'atna' ? t('Assistant ATNA', 'ATNA assistant') : CHANNEL_LABELS[row.channel][lang]}
                      {row.best && <span className="px-2 py-0.5 text-[10px] bg-[#4F46E5] text-white rounded-full font-bold" data-id="element-167" data-mp="hco4t41dx0fr">
                          {t('MEILLEUR', 'BEST')}
                        </span>}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-600" data-id="element-168" data-mp="2f5fo0ksol7s">{row.sent}</td>
                  <td className="px-4 py-3" data-id="element-169" data-mp="2evm630jjvxc">
                    <div className="flex items-center gap-2" data-id="element-170" data-mp="3ujg54sfwxs4t">
                      <span className="w-10" data-id="element-171" data-mp="3ukr66khz4ls8">{row.open} %</span>
                      <div className="w-24 h-2 bg-slate-100 rounded-full overflow-hidden" data-id="element-172" data-mp="3prfxedtlpuxo">
                        <div className="h-full bg-[#4F46E5]" style={{
                      width: `${row.open}%`
                    }} data-id="element-173" data-mp="3ku67kx2avh9b" />
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3" data-id="element-174" data-mp="3awrth43un4k6">
                    <div className="flex items-center gap-2" data-id="element-175" data-mp="36ueowj0u9coz">
                      <span className="w-10" data-id="element-176" data-mp="1mbb8cg29y9ht">{row.interact} %</span>
                      <div className="w-24 h-2 bg-slate-100 rounded-full overflow-hidden" data-id="element-177" data-mp="1mb1jivsyu8dp">
                        <div className="h-full bg-emerald-500" style={{
                      width: `${row.interact}%`
                    }} data-id="element-178" data-mp="1qbboq68xouri" />
                      </div>
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </section>

      {/* Coverage & funnel */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-179" data-mp="17n6oh8rhjrxg">
        <section className="bg-white p-6 rounded-xl shadow-sm border border-slate-100 flex flex-col" data-id="element-180" data-mp="1ckzsbv5bg9uk">
          <h2 className="text-lg font-bold text-[#0F172A] font-heading mb-4" data-id="element-181" data-mp="1hcq8wiuujtuf">
            {t('Couverture géographique', 'Geographic coverage')}
          </h2>

          <div className="flex gap-2 mb-6 overflow-x-auto pb-2" data-id="element-182" data-mp="juq42rk9znuz">
            {sectors.map(s => <button key={s.id} onClick={() => setSelectedSector(s.id)} className={`px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors border ${selectedSector === s.id ? 'bg-[#0F172A] text-white border-[#0F172A]' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`} data-id="element-183" data-mp="otugdr2hy1pj">
                <span className="flex items-center gap-2" data-id="element-184" data-mp="16nu4uf9v350">
                  <span className={`w-2 h-2 rounded-full ${s.status === 'green' ? 'bg-emerald-500' : s.status === 'yellow' ? 'bg-amber-400' : 'bg-red-500'}`} data-id="element-185" data-mp="154dh2ppeq2p" />
                  {sectorName(s.id)}
                </span>
              </button>)}
          </div>

          <div className="flex-1 bg-slate-50 rounded-xl border border-slate-100 p-5 flex flex-col justify-center" data-id="element-186" data-mp="f06na0xo8zks">
            <div className="flex items-center gap-3 mb-4 flex-wrap" data-id="element-187" data-mp="ylf9i5eccskl">
              <MapPinIcon className="w-6 h-6 text-[#4F46E5]" data-id="element-188" data-mp="13ip748bvwaji" />
              <h3 className="text-xl font-bold text-[#0F172A]" data-id="element-189" data-mp="omhnb94e1b4r">
                {sectorName(active.id)}
              </h3>
              <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${active.status === 'green' ? 'bg-emerald-100 text-emerald-700' : active.status === 'yellow' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`} data-id="element-190" data-mp="tl18h7b5qiw2">
                {active.status === 'green' ? t('Forte portée', 'Strong reach') : active.status === 'yellow' ? t('Portée moyenne', 'Average reach') : t('Faible portée', 'Low reach')}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-4" data-id="element-191" data-mp="2k0i18lqt2l4j">
              {[{
              label: t('Citoyens inscrits', 'Registered citizens'),
              value: active.citizens.toLocaleString(locale)
            }, {
              label: t('Cartes bciti+ actives', 'Active bciti+ cards'),
              value: active.cards.toLocaleString(locale)
            }, {
              label: t("Taux d'ouverture moy.", 'Avg. open rate'),
              value: `${active.openRate} %`
            }, {
              label: t('Avis envoyés', 'Notices sent'),
              value: active.notices.toLocaleString(locale)
            }].map(item => <div key={item.label} className="bg-white p-3 rounded-lg border border-slate-200 shadow-sm" data-id="element-192" data-mp="3hq37f1ny8ina">
                  <div className="text-xs text-slate-500 mb-1" data-id="element-193" data-mp="3qpq8tngohogc">{item.label}</div>
                  <div className="font-bold text-lg text-[#0F172A]" data-id="element-194" data-mp="1f1li8g2hynb1">{item.value}</div>
                </div>)}
              <div className="col-span-2 bg-indigo-50 p-3 rounded-lg border border-indigo-100 shadow-sm flex justify-between items-center gap-3" data-id="element-195" data-mp="1ayymi42pgi8k">
                <div className="text-sm font-medium text-indigo-900" data-id="element-196" data-mp="111k624gs7tg9">
                  {t("Questions posées à l'IA", 'Questions asked to the AI')}
                </div>
                <div className="font-bold text-xl text-[#4F46E5]" data-id="element-197" data-mp="1yoj3wij7n7fq">
                  {active.questions.toLocaleString(locale)}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-white p-6 rounded-xl shadow-sm border border-slate-100" data-id="element-198" data-mp="1ox6ctt58klar">
          <h2 className="text-lg font-bold text-[#0F172A] font-heading mb-6" data-id="element-199" data-mp="1jybxx2ae3gvx">
            {t('Entonnoir de communication', 'Communication funnel')}
          </h2>
          <div className="space-y-2" data-id="element-200" data-mp="2co2b99476eqz">
            {funnel.map(step => <div key={step.label} data-id="element-201" data-mp="ar1ms0ulf3z3">
                <div className="flex items-center justify-between mb-1 px-1 gap-3" data-id="element-202" data-mp="as37kx1ev4u0">
                  <span className="text-sm font-medium text-slate-700" data-id="element-203" data-mp="erv8qooh68pc">
                    {step.label}
                  </span>
                  <span className="text-sm font-bold text-slate-900" data-id="element-204" data-mp="znrk59ocqbr">
                    {step.value}
                  </span>
                </div>
                <div className="w-full h-8 bg-slate-100 rounded-lg overflow-hidden flex items-center" data-id="element-205" data-mp="3n8ozobs36xai">
                  <div className={`h-full ${step.color} flex items-center justify-end px-3 transition-all duration-1000`} style={{
                width: `${step.percent}%`
              }} data-id="element-206" data-mp="3s8bayfocu3go">
                    <span className="text-xs font-bold text-white drop-shadow-md" data-id="element-207" data-mp="3j8688iu3vt8u">
                      {step.percent} %
                    </span>
                  </div>
                </div>
              </div>)}
          </div>
        </section>
      </div>

      {/* Topics & questions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-208" data-mp="1rkghi0lin35b">
        <section className="bg-white p-6 rounded-xl shadow-sm border border-slate-100" data-id="element-209" data-mp="1mldlt6rfe9a0">
          <h2 className="text-lg font-bold text-[#0F172A] font-heading mb-6" data-id="element-210" data-mp="20c0d4duuwww2">
            {t('Sujets les plus engageants', 'Most engaging topics')}
          </h2>
          <div className="space-y-4" data-id="element-211" data-mp="1vjfjpdfv4lt2">
            {topics.map((item, i) => <div key={item.topic} className="flex items-center justify-between gap-3 p-3 hover:bg-slate-50 rounded-lg transition-colors border border-transparent hover:border-slate-100" data-id="element-212" data-mp="2a8v7qlo0ed52" data-mpi={i}>
                <div className="flex items-center gap-3 min-w-0" data-id="element-213" data-mp="2j8rzwx9lxu4v">
                  <div className="w-6 h-6 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center text-xs font-bold flex-shrink-0" data-id="element-214" data-mp="2fa120nxbi8gp">
                    {i + 1}
                  </div>
                  <div className="min-w-0" data-id="element-215" data-mp="kxb8466y8z9b">
                    <div className="font-medium text-slate-900 truncate" data-id="element-216" data-mp="mcxeograhp0r">
                      {item.topic}
                    </div>
                    <div className="text-xs text-slate-500" data-id="element-217" data-mp="rafffjv5kxxo">
                      {item.sent} {t('avis envoyés', 'notices sent')}
                    </div>
                  </div>
                </div>
                <div className="flex gap-4 text-right flex-shrink-0" data-id="element-218" data-mp="10b5aglclakac">
                  <div data-id="element-219" data-mp="3mpg8byjbmqw">
                    <div className="text-xs text-slate-500" data-id="element-220" data-mp="3nh22v2by63z">
                      {t('Ouverture', 'Open')}
                    </div>
                    <div className="font-medium text-[#0F172A]" data-id="element-221" data-mp="7lolxys3bnje">{item.open}</div>
                  </div>
                  <div data-id="element-222" data-mp="cl16gcvhoiit">
                    <div className="text-xs text-slate-500" data-id="element-223" data-mp="hdaufnd1n8ig">{t('Clics', 'Clicks')}</div>
                    <div className="font-medium text-[#0F172A]" data-id="element-224" data-mp="3g3ko6lgjk5zd">{item.click}</div>
                  </div>
                  <div className="bg-indigo-50 px-2 py-1 rounded text-center min-w-[3rem]" data-id="element-225" data-mp="3l2e38ibh851n">
                    <div className="text-[10px] text-[#4F46E5] font-bold uppercase" data-id="element-226" data-mp="3dj4r4hbl5s8w">
                      {t('Questions', 'Questions')}
                    </div>
                    <div className="font-bold text-[#4F46E5]" data-id="element-227" data-mp="38kuxib5iezpe">{item.q}</div>
                  </div>
                </div>
              </div>)}
          </div>
        </section>

        <section className="bg-white p-6 rounded-xl shadow-sm border border-slate-100 flex flex-col" data-id="element-228" data-mp="2zilr9xh9xsht">
          <div className="flex items-center gap-2 mb-6" data-id="element-229" data-mp="2ulva16tqgqut">
            <MessageSquareIcon className="w-5 h-5 text-[#4F46E5]" data-id="element-230" data-mp="2pt24whucrgi7" />
            <h2 className="text-lg font-bold text-[#0F172A] font-heading" data-id="element-231" data-mp="2prr14i0wq8r3">
              {t('Insights ATNA : questions fréquentes', 'ATNA insights: frequent questions')}
            </h2>
          </div>

          <div className="bg-slate-50 rounded-xl p-1 flex-1" data-id="element-232" data-mp="2bsrbb71nrntc">
            <ul className="space-y-1" data-id="element-233" data-mp="26ups8m48fohv">
              {questions.map((q, i) => <li key={q} className="flex items-center gap-3 p-3 bg-white rounded-lg shadow-sm" data-id="element-234" data-mp="26x290vy97stc" data-mpi={i}>
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${i < 3 ? 'bg-[#4F46E5] text-white' : 'bg-slate-100 text-slate-500'}`} data-id="element-235" data-mp="219qlbtflmgau">
                    {i + 1}
                  </span>
                  <span className="text-sm font-medium text-slate-700" data-id="element-236" data-mp="2fydb6p2uqqgb">{q}</span>
                  {i < 3 && <TrendingUpIcon className="w-4 h-4 text-emerald-500 ml-auto flex-shrink-0" data-id="element-237" data-mp="2kyhjhrtlwevg" />}
                </li>)}
            </ul>
          </div>
        </section>
      </div>
    </div>;
}